import { getRootLink } from '@/lib/utils';
import { useAuthStore } from '@/store';
import { QuestionLinkType } from '@/types/api/question';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { useEffect, useState } from 'react';
import { notificationsProtectedAxios } from '../lib/axiosInstance';
import { firebaseApp } from '../lib/firebase';
export const useFCM = () => {
  const [token, setToken] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(false);
  const authtoken = useAuthStore((state) => state.token);

  useEffect(() => {
    // Check if browser supports FCM
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setIsSupported(true);
    }
  }, []);

  useEffect(() => {
    if (!isSupported || !authtoken) return;

    const messaging = getMessaging(firebaseApp);

    // Request notification permission
    const requestPermission = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          console.log('Notification permission granted.');

          // Register service worker
          const registration = await navigator.serviceWorker.register(
            '/firebase-messaging-sw.js',
            {
              scope: '/',
            }
          );

          // Get FCM token
          const currentToken = await getToken(messaging, {
            vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
            serviceWorkerRegistration: registration,
          });

          if (currentToken) {
            setToken(currentToken);

            // Save token to backend
            try {
              await notificationsProtectedAxios.post('/fcm-token', {
                token: currentToken,
              });
            } catch (error) {
              console.error('Error saving FCM token:', error);
            }
          } else {
            console.log(
              'No registration token available. Request permission to generate one.'
            );
          }
        } else {
          console.log('Unable to get permission to notify.');
        }
      } catch (err) {
        console.error('An error occurred while retrieving token:', err);
      }
    };

    // Handle foreground messages
    const unsubscribe = onMessage(messaging, (payload) => {
      const root = getRootLink(payload.data?.questionType as QuestionLinkType);
      const link = `/${root}/question/${payload.data?.questionSlug}?focusedStoryId=${payload.data?.storyId ?? ''}&focusedCommentId=${payload.data?.commentId ?? ''}`;
      // Create and show notification
      if (payload.notification) {
        new Notification(payload.notification.title || '', {
          body: payload.notification.body,
          icon: '/logo.png',
          data: { url: link },
        }).onclick = () => {
          window.open(link, '_blank');
        };
      }
    });

    requestPermission();

    return () => {
      unsubscribe();
    };
  }, [isSupported, authtoken]);

  return { token, isSupported, authtoken };
};
